import React from 'react';
import styled from 'styled-components';
import { BeatDevice, type FeatureBeatsFrame } from './FeatureBeats';
import { TypedQuizNote } from './TypedQuizNote';
import { PAGE_GUTTER_DESKTOP } from '../../../theaWeb/lib/pageGutter';

/**
 * FeatureStack — the un-carouseled sibling of FeatureBeats: every frame gets
 * its own dedicated card, read one by one as the visitor scrolls (Kate
 * 2026-07-20, for the /welcome2 campaign homepage — a paid visitor shouldn't
 * have to wait on a rotation to see the pitch).
 *
 * Reuses FeatureBeats' frame shape and BeatDevice (framed-iPhone screenshot)
 * so the two layouts can't drift visually. Cards mirror the BeatCard look:
 * warm cream container, phone left / copy right on desktop — with the phone
 * side alternating per card for scroll rhythm — copy-over-phone stacked on
 * mobile. No auto-advance, no dots: nothing moves, so there's nothing to
 * pause (and nothing for Happo to catch mid-flight).
 */

/* Wider + bigger than the homepage carousel (Kate 2026-07-20): the stack is
   the page's main pitch, so it stretches to the testimonials' 1280px rail
   and renders the device ~75% larger than the carousel's. */
/* Top padding: shallow on DESKTOP (Kate 2026-07-21) so — paired with the
   hero's compactBottom — the first card peeks above the fold under the CTA
   as a scroll cue. MOBILE top padding was doubled (Kate 2026-07-23) for
   more breathing room between the CTA and the first card (80px + the hero's
   40px compactBottom ≈ double the prior ~60px gap). */
// NO MAX-WIDTH, NO MARGIN:AUTO AT >=1024px (THE-1161, Kate 2026-10-26) —
// ONLY WHEN `$wideGutter` IS SET (giftGuideHubFlag.ts, Kate 2026-10-27:
// hold this off production until she can review it at a preview link). A
// capped-and-centered 1280px box put this section's left edge at 224px on a
// 1728px first-time homepage while the hero wordmark and Coming Up strip
// (ComingUpStrip's Inner, same PAGE_GUTTER_DESKTOP) sat at 144px — two left
// edges on one page. Below 1024px the cap never engaged anyway (viewport
// never exceeds 1280px there), so mobile/tablet are unchanged either way.
const Section = styled.section<{ $wideGutter?: boolean }>`
  max-width: 1280px;
  margin: 0 auto;
  padding: 80px 16px 56px;
  display: grid;
  /* Mobile gap between the 4 stacked cards doubled (Kate 2026-07-23) for more
     separation on phones; desktop (32px) unchanged. */
  gap: 40px;
  @media (min-width: 768px) {
    padding: 28px 32px 80px;
    gap: 32px;
  }
  @media (min-width: 1024px) {
    ${({ $wideGutter }) =>
      $wideGutter &&
      'max-width: none; padding-left: ' +
        PAGE_GUTTER_DESKTOP +
        '; padding-right: ' +
        PAGE_GUTTER_DESKTOP +
        ';'}
  }
`;

const DEVICE_MOBILE_WIDTH = 330;
const DEVICE_DESKTOP_WIDTH = 368; /* ≈ 210px carousel size × 1.75 */

const Card = styled.div<{ $flip: boolean }>`
  background: ${({ theme }) => theme.color.cream};
  border-radius: 28px;
  overflow: hidden;
  display: grid;
  grid-template-areas: 'copy' 'phone';
  @media (min-width: 768px) {
    grid-template-columns: ${({ $flip }) => ($flip ? '1fr 42%' : '42% 1fr')};
    grid-template-areas: ${({ $flip }) => ($flip ? "'copy phone'" : "'phone copy'")};
  }
`;

const PhoneArea = styled.div<{ $flip: boolean }>`
  grid-area: phone;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 24px 24px 0;
  @media (min-width: 768px) {
    padding: ${({ $flip }) => ($flip ? '40px 40px 0 0' : '40px 0 0 40px')};
  }
`;

const CopyArea = styled.div`
  grid-area: copy;
  padding: 26px 24px 0;
  text-align: left;
  @media (min-width: 768px) {
    padding: 52px 48px 52px 44px;
    align-self: center;
  }
`;

const Label = styled.h3`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => theme.color.inkSoft};
  margin: 0;
  @media (min-width: 768px) {
    font-size: 38px;
  }
`;

const Body = styled.p`
  font-size: 18px;
  color: #665e58;
  line-height: 1.5;
  margin: 8px 0 0;
  max-width: 30rem;
  @media (min-width: 768px) {
    font-size: 19px;
    line-height: 1.55;
    margin-top: 16px;
  }
`;

export interface FeatureStackProps {
  frames: FeatureBeatsFrame[];
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to
   * `false` so every existing story/test and real visitor keeps rendering
   * today's production layout when the hub gate is off. `LandingPage.tsx`
   * resolves the flag once and passes it down.
   */
  wideGutterEnabled?: boolean;
}

export const FeatureStack: React.FC<FeatureStackProps> = ({ frames, wideGutterEnabled = false }) => (
  <Section aria-label="How Thea works" $wideGutter={wideGutterEnabled}>
    {frames.map((frame, i) => (
      <Card key={frame.label} $flip={i % 2 === 1}>
        <PhoneArea $flip={i % 2 === 1}>
          <BeatDevice
            frame={frame}
            mobileWidth={DEVICE_MOBILE_WIDTH}
            desktopWidth={DEVICE_DESKTOP_WIDTH}
            screenOverlay={
              frame.typedExample ? (
                <TypedQuizNote text={frame.typedExample} />
              ) : undefined
            }
          />
        </PhoneArea>
        <CopyArea>
          <Label>{frame.label}</Label>
          <Body>{frame.body}</Body>
        </CopyArea>
      </Card>
    ))}
  </Section>
);
